/** Formatting helpers shared by the user console tabs. */
import { ApiError } from "@/lib/api";

export function describeError(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

/** Dates arrive as UTC ISO strings; show them in one predictable format. */
export function formatDate(value: string | null, withTime = false): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

export function formatMoney(
  amount: number | null,
  currency: string | null
): string {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  } catch {
    return `${amount} ${currency ?? ""}`.trim();
  }
}

/**
 * "in 12 days" / "3 days ago" — the arithmetic support would otherwise do in
 * their head next to every date that decides access.
 */
export function relativeDate(value: string | null): string | null {
  if (!value) return null;
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.round((then.getTime() - Date.now()) / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  const n = Math.abs(days);
  const unit =
    n < 45 ? [n, "day"] : n < 365 ? [Math.round(n / 30), "month"] : [Math.round(n / 365), "year"];
  const label = `${unit[0]} ${unit[1]}${unit[0] === 1 ? "" : "s"}`;
  return days > 0 ? `in ${label}` : `${label} ago`;
}
